<template>
  <div
    class="w-full bg-zt-white rounded-zt-md overflow-hidden px-4 pt-4"
    :class="[boxLogin.styles.shadow]"
    :style="[boxLogin.styles.box]"
  >
    <div
      class="flex flex-col items-center p-6"
      :class="maxFailsExceeded ? ['md:py-6', 'md:px-0'] : null"
    >
      <div
        class="mb-4 flex justify-center h-full"
        :class="[boxLogin.sizeLogo === 'full' ? 'w-full' : 'w-56']"
      >
        <img class="format-img" :src="boxLogin.logoUrl" alt="" />
      </div>
      <div class="content">
        <div class="flex items-center justify-center mb-10 md:mt-10">
          <div class="hidden md:block w-1/3">
            <div
              v-if="wrongCode || maxFailsExceeded || resendExceeded || notSendMail "
              class="mfa-logo-verification flex justify-center items-center bg-zt-danger rounded-full"
            >
              <zt-icon class="text-white text-5xl" name="shield-times" />
            </div>
            <div
              class="mfa-logo-verification flex justify-center items-center bg-zt-secondary-100 rounded-full"
              v-else
            >
              <zt-icon class="text-white text-5xl" name="shield-asterisk" />
            </div>
          </div>
          <div
            class="text-center md:text-left md:ml-5"
            :class="maxFailsExceeded ? ['mt-10', 'md:mt-0'] : null"
          >
            <div
              class="flex items-center justify-start md:justify-center md:block"
            >
              <div class="md:hidden w-1/5">
                <div
                  v-if="wrongCode || maxFailsExceeded || resendExceeded || notSendMail"
                  class="mfa-logo-verification-mobile flex justify-center items-center bg-zt-danger rounded-full"
                >
                  <zt-icon class="text-white text-3xl" name="shield-times" />
                </div>
                <div
                  v-else
                  class="mfa-logo-verification-mobile flex justify-center items-center bg-zt-secondary-100 rounded-full"
                >
                  <zt-icon class="text-white text-3xl" name="shield-asterisk" />
                </div>
              </div>
              <div
                class="text-zt-primary-200 text-xl md:text-2xl font-medium md:mb-2"
              >
                {{ $t('message.mfa.code_authentication.title') }}
              </div>
            </div>
            <div
              class="my-2 md:mb-2 mfa-wrong-code text-white bg-zt-danger rounded p-2"
              v-if="wrongCode"
            >
              {{ $t('message.mfa.code_authentication.sub_title_wrong_code') }}
            </div>
            <div v-else-if="maxFailsExceeded" class="mt-12 mb-20 md:my-2">
              <p class="mfa-description text-base">
                {{
                  $t(
                    'message.mfa.code_authentication.maxFailsExceededDescription'
                  )
                }}
              </p>
            </div>
            <div v-else-if="resendExceeded" class="mt-12 mb-20 md:my-2">
              <p class="mfa-description text-base">
                {{
                  $t(
                    'message.mfa.code_authentication.SYSTEM_MFA_AUTH_MAX_RESEND_EXCEEDED'
                  )
                }}
              </p>
            </div>
            <div v-else-if="notSendMail" class="mt-12 mb-20 md:my-2">
              <p class="mfa-description text-base">
                {{
                  $t(
                    'message.mfa.code_authentication.COULD_NOT_SEND_MAIL'
                  )
                }}
              </p>
            </div>
            <div v-else class="my-2">
              <p class="mfa-description text-base">
                {{ $t('message.mfa.code_authentication.sub_title_valid_code') }}
              </p>
              <p class="font-bold">{{ userEmail }}</p>
            </div>
            <div v-if="!maxFailsExceeded">
              <div class="mfa-description text-base" v-if="buttonResendEnabled">
                <span>
                  {{
                    $t('message.mfa.code_authentication.sub_title_expired_code')
                  }}
                  &nbsp;</span
                ><span
                  class="text-black font-bold underline cursor-pointer"
                  @click="sendValidationCodeAgain"
                  data-vs-link="auth-mfa-link-sendvalidationcodeagain"
                  >{{
                    $t('message.mfa.code_authentication.sub_title_resend_code')
                  }}
                </span>
              </div>
            </div>
            <div
              class="mfa-timer text-base"
              v-if="!buttonResendEnabled && !(maxFailsExceeded || resendExceeded || notSendMail)"
            >
              <span>
                {{
                  $t(
                    'message.mfa.code_authentication.sub_title_time_expired_code'
                  )
                }}
              </span>
              <Timer
                @endTime="enableResendButton"
                :timerStart="timerStart"
              ></Timer>
            </div>
          </div>
        </div>
        <div v-if="!(maxFailsExceeded || resendExceeded || notSendMail)" class="flex justify-between mb-10 w-full">
          <input
            id="codePart1"
            value=""
            class="bg-zt-dark-200 mfa-input-code text-center text-zt-lg"
            maxlength="1"
            :type="isMobile ? 'number' : 'text'"
            placeholder="0"
            min="0"
            max="9"
            data-vs-input="auth-mfa-input-codepart1"
          />
          <input
            id="codePart2"
            value=""
            class="bg-zt-dark-200 mfa-input-code text-center text-zt-lg"
            maxlength="1"
            :type="isMobile ? 'number' : 'text'"
            placeholder="0"
            min="0"
            max="9"
            data-vs-input="auth-mfa-input-codepart2"
          />
          <input
            id="codePart3"
            value=""
            class="bg-zt-dark-200 mfa-input-code text-center text-zt-lg"
            maxlength="1"
            :type="isMobile ? 'number' : 'text'"
            placeholder="0"
            min="0"
            max="9"
            data-vs-input="auth-mfa-input-codepart3"
          />
          <input
            id="codePart4"
            value=""
            class="bg-zt-dark-200 mfa-input-code text-center text-zt-lg"
            maxlength="1"
            :type="isMobile ? 'number' : 'text'"
            placeholder="0"
            min="0"
            max="9"
            data-vs-input="auth-mfa-input-codepart4"
          />
          <input
            id="codePart5"
            value=""
            class="bg-zt-dark-200 mfa-input-code text-center text-zt-lg"
            maxlength="1"
            :type="isMobile ? 'number' : 'text'"
            placeholder="0"
            min="0"
            max="9"
            data-vs-input="auth-mfa-input-codepart5"
          />
          <input
            id="codePart6"
            value=""
            class="bg-zt-dark-200 mfa-input-code text-center text-zt-lg"
            maxlength="1"
            :type="isMobile ? 'number' : 'text'"
            placeholder="0"
            min="0"
            max="9"
            data-vs-input="auth-mfa-input-codepart6"
          />
        </div>
      </div>
      <div
        class="flex mb-2 w-full"
        :class="maxFailsExceeded ? 'justify-center' : 'justify-between'"
      >
        <div class="w-1/2" :class="maxFailsExceeded ? 'mr-0' : 'mr-4'">
          <zt-button
            outlined
            style="color:#4f41a3 !important;"
            @click="backToLogin"
            class="w-full"
            data-vs-button="auth-mfa-btn-backtologin"
            >{{
              maxFailsExceeded
                ? $t('message.mfa.code_authentication.go_to_login_btn')
                : $t('message.mfa.code_authentication.go_back_btn')
            }}</zt-button
          >
        </div>
        <div v-if="!maxFailsExceeded" class="w-1/2">
          <zt-button
            class="w-full"
            @click="sendMFAValidationCode()"
            :disabled="!inputComplete"
            :loader="sending"
            :loaderTxt="$t('message.mfa.code_authentication.sending_btn')"
            data-vs-button="auth-mfa-btn-sendmfavalidationcode"
            >{{ $t('message.mfa.code_authentication.send_btn') }}</zt-button
          >
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import loginCustomParams from '@/modules/Auth/mixins/loginCustomParams'
import Timer from '@/modules/Auth/components/mfa/timer.vue'
import MFA from '@/apiClient/Mfa.js'
import Lo from 'lodash'
import { useWindowResize } from '@sichern/ui-elements'
import SichernIntegration from '@/apiClient/SichernIntegration'

export default {
  name: 'ztMFACodeVerification',
  data() {
    return {
      objectLoginParams: {},
      buttonResendEnabled: false,
      wrongCode: false,
      sending: false,
      inputComplete: false,
      prevRoute: null,
      reloadPage: false,
      maxFailsExceeded: false,
      timerStart: false,
      windowSize: useWindowResize(this),
      resendExceeded: false,
      notSendMail: false,
      hasIntegrationSichernPeople: false
    }
  },
  components: {
    Timer
  },
  mixins: [loginCustomParams],
  computed: {
    ...mapGetters({
      getLoginCustomParams: 'auth/getLoginCustomParams',
      getUser: 'getUser',
      getPrevRoute: 'getPrevRoute',
      getJwt: 'getJwt'
    }),
    userEmail() {
      if (this.$route.params.email && this.$route.params.email.length) {
        return this.$route.params.email
      } else {
        return this.getUser.mail
      }
    },
    isFromLogin() {
      return this.getPrevRoute === 'login'
    },
    isMobile() {
      return this.windowSize.width < 1024
    }
  },
  methods: {
    ...mapActions({
      setActions: 'setActions',
      setStateNotOnBoarding: 'setStateNotOnboarding',
      setPrevRouteMfaValidation: 'setPrevRouteMfaValidation',
      setHasIntegrationSichernPeople: 'setHasIntegrationSichernPeople',
    }),
    sendMFAValidationCode() {
      const inputs = document.getElementsByClassName('mfa-input-code')
      const codeMFA = [].map
        .call(inputs, function(input) {
          return input.value
        })
        .join('')
      this.sending = true
      this.wrongCode = false
      if (this.isFromLogin) {
        this.mfaClient
          .validateAuthCode(codeMFA)
          .then(() => {
            this.authorizedMfaAccess()
          })
          .catch(e => {
            this.checkError(e)
          })
      } else {
        this.mfaClient
          .validateAuthCodeFromEmail(codeMFA)
          .then(response => {
            this.authorizedMfaAccess()
          })
          .catch(e => {
            this.checkError(e)
          })
      }
    },
    onlyNumbers(textInput, filter) {
      ;[
        'input',
        'keydown',
        'keyup',
        'mousedown',
        'mouseup',
        'select',
        'contextmenu',
        'drop'
      ].forEach(function(event) {
        textInput.addEventListener(event, function() {
          if (filter(this.value)) {
            this.oldValue = this.value
            this.oldSelectionStart = this.selectionStart
            this.oldSelectionEnd = this.selectionEnd
          } else if (this.hasOwnProperty('oldValue')) {
            this.value = this.oldValue
            this.setSelectionRange(this.oldSelectionStart, this.oldSelectionEnd)
          } else {
            this.value = ''
          }
        })
      })
    },
    eventsInputsCode() {
      const inputs = [].slice.call(
        document.getElementsByClassName('mfa-input-code')
      )
      inputs.forEach((input, index, array) => {
        input.onkeypress = event => {
          let keyChar = String.fromCharCode(event.keyCode)
          if (!/^-?\d*$/.test(keyChar)) return event.preventDefault()
        }
        input.oninput = () => {
          this.checkInputComplete(array)
          let nexInput = index + 1
          if (nexInput < array.length) {
            array[nexInput].focus()
          }
        }
        input.onpaste = event => {
          let clip = event.clipboardData.getData('text').trim()
          if (!/^-?\d*$/.test(clip)) return event.preventDefault()
          const clipData = [...clip]
          Lo.map(inputs, (input, index) => {
            input.focus()
            let data = clipData[index] ? clipData[index] : ''
            input.value = data
            input.blur()
          })
          this.checkInputComplete(array)
        }
      })
    },
    sendValidationCodeAgain() {
      this.timerStart = true
      this.buttonResendEnabled = false
      this.wrongCode = false
      this.resetCodeFields()
      this.sendVerificationCode()
      this.getResendStatus()
    },
    resetCodeFields() {
      const inputs = document.getElementsByClassName('mfa-input-code')
      Lo.map(inputs, input => {
        input.value = ''
      })
    },
    sendVerificationCode(firstTime) {
      if (!this.isFromLogin && firstTime && !this.reloadPage) {
        this.mfaClient.sendEmailVerificationCode()
      } else {
        this.mfaClient.sendVerificationCode().catch(error => {
          this.checkError(error)
        })
      }
      
    },
    checkError(e) {
      let error = e.response.data
      let code = error.codes[0].code
      switch (code) {
        case 'SYSTEM_MFA_WRONG_CODE':
          this.wrongCode = true
          this.resetCodeFields()
          break
        case 'SYSTEM_MFA_AUTH_MAX_FAILS_EXCEEDED':
          this.maxFailsExceeded = true
          break
        case 'SYSTEM_MFA_AUTH_MAX_RESEND_EXCEEDED':
          this.resendExceeded = true
          break
        case 'COULD_NOT_SEND_MAIL':
          this.notSendMail = true
          break
        default:
          break
      }
      this.sending = false
    },
    backToLogin() {
      this.$store.dispatch('clearState')
      this.$router.push({ name: 'login' })
    },
    enableResendButton() {
      this.timerStart = false
      this.buttonResendEnabled = true
      this.wrongCode = false
      this.resendExceeded = false
      this.notSendMail = false
    },
    async authorizedMfaAccess() {
      try {
        if (this.getUser.estado_empresa === 2 ){
          this.verifyUserOnBoarding()
        } else {
          // await this.checkActiveSichernPeopleIntegration()
          await this.setActions()
          await this.setStateNotOnBoarding(true)
          await this.$router.push('/')
        }
      } catch (error) {}
    },
    checkInputComplete(array) {
      let inp = array
      let code = Lo.filter(inp, function(input) {
        return input.value !== ''
      })
      if (code.length === 6) {
        this.inputComplete = true
      } else {
        this.inputComplete = false
      }
    },
    getResendStatus() {
      this.mfaClient.getResendStatus().then(() => {
        this.$router.push({
          name: 'mfaUserBlock'
        })
      })
    },
    verifyUserOnBoarding() {
      localStorage.setItem('jwt', this.getJwt)
      location.replace(
        window.location.origin + '/s/home/' + this.getUser.id
      )
      this.$store.dispatch('clearState')
    },
    async checkActiveSichernPeopleIntegration() {
      const type = 'VP_ABSENCES_HOLIDAYS'
      try {
        const res = await this.vi.checkIntegrationSichernPeople(type)
        this.hasIntegrationSichernPeople = res.data.data.enabled
        this.setHasIntegrationSichernPeople(this.hasIntegrationSichernPeople)
      } catch (error) {
        console.error('Error checking Sichern People integration:', error)
        this.setHasIntegrationSichernPeople(false)
      }
    },
  },
  mounted() {
    let inputs = document.getElementsByClassName('mfa-input-code')
    inputs[0].focus()
    this.eventsInputsCode()
    let prevRoute = localStorage.getItem('prevRoute')
    this.setPrevRouteMfaValidation(prevRoute)
    this.sendVerificationCode(true)
  },
  created() {
    if (!this.getJwt) {
      this.$router.push('/login')
    }
    this.mfaClient = new MFA()
    this.vi = new SichernIntegration()
    this.objectLoginParams = this.getLoginCustomParams.loginCustom
  },
  beforeRouteEnter(to, from, next) {
    next(vm => {
      if (from.name) {
        vm.timerStart = true
        localStorage.setItem('prevRoute', from.name)
      } else {
        vm.reloadPage = true
      }
    })
  }
}
</script>

<style scoped lang="scss">
.mfa-input-code {
  width: 2.563rem;
  height: 2.438rem;
}

.mfa-logo-verification {
  width: 6.25rem;
  height: 6.25rem;
}

.mfa-logo-verification-mobile {
  width: 3.19rem;
  height: 3.19rem;
}

.mfa-description {
  line-height: 1.43;
  color: #4f4f4f;
}

.mfa-timer {
  color: #949494;
}

input[type='number'] {
  -moz-appearance: textfield;
}
input[type='number']::-webkit-inner-spin-button,
input[type='number']::-webkit-outer-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

::v-deep {
  .zt-button {
    box-shadow: none;
  }
}
</style>
